import {forwardRef, Ref, useEffect, useState} from 'react';
import {Box, Stack} from '@mui/material';
import {useTranslation} from 'react-i18next';
import {translations} from '../../i18n/translations';
import moment, {isMoment} from 'moment';
import {PeriodicityForm, PeriodicityFormType} from '../periodicity-form/periodicity-form';
import {ScheduleField} from '../schedule-field/schedule-field';
import Switch from '../switch/switch';
import {useForm, UseFormReturn} from 'react-hook-form';
import {FlatInputProps} from '../flat-input/flat-input';
import {DEFAULT_FORM_VALIDATIONS} from '../../utils/constants';
import {ActivityTypeSchedule, Frequency, SchedulerTypes} from '../../types';
import {Modal} from '../modal/modal';
import {DynamicFormFieldShape} from '../dynamic-form';

export type ActivityTypeScheduleFormProps = FlatInputProps & {
  type: SchedulerTypes.ActivityType | SchedulerTypes.CareService;
  open: boolean;
  parentFormInstance: UseFormReturn<DynamicFormFieldShape>;
  startAtTimeId: string;
  startAtDateId: string;
  readonly?: boolean;
  onClose?: () => void;
};

export const ActivityTypeScheduleForm = forwardRef(
  (props: ActivityTypeScheduleFormProps, ref: Ref<HTMLInputElement>) => {
    const {t} = useTranslation();
    const {startAtTimeId, startAtDateId, ...restProps} = props;

    // trigger nested schedule modal
    const [openSchedule, setOpenSchedule] = useState(false);

    // control display of periodicity form
    const [displayRepetition, setDisplayRepetition] = useState(false);

    // enqueue react-hook-form
    const formInstance = useForm<PeriodicityFormType>({
      defaultValues: {
        frequencyValue: 1,
        frequency: {
          key: Frequency.Daily,
          label: t(translations.periodicity_form.fields.frequency.daily),
        },
        startDate: moment().toISOString(),
        endDate: undefined,
        endHour: undefined,
      },
      ...DEFAULT_FORM_VALIDATIONS,
    });

    // control display of periodicity form
    const handleDisplayRepetition = () => {
      if (!displayRepetition === false)
        formInstance.unregister(['frequency', 'frequencyValue', 'endDate', 'endHour', 'days']);
      setDisplayRepetition((state) => !state);
    };

    const onSubmit = () => {
      // get instance values
      const [frequency, frequencyValue, endDate, endHour, days] = formInstance.getValues([
        'frequency.key',
        'frequencyValue',
        'endDate',
        'endHour',
        'days',
      ]);

      let updatedData: ActivityTypeSchedule | Record<any, never> = {};

      if (displayRepetition) {
        updatedData = {
          frequency,
          frequencyValue,
          // @ts-ignore
          endDate: endDate ? moment(endDate).toISOString() : null,
          // @ts-ignore
          endHour: endHour ? moment(endHour).toISOString() : null,
          days,
        };

        if (updatedData.frequency !== Frequency.Weekly) delete updatedData.days;
      }

      // update upstream form
      props.parentFormInstance.setValue(props.id ?? '', JSON.stringify(updatedData), {
        shouldDirty: true,
        shouldValidate: true,
      });

      // revalidate parent form
      props.parentFormInstance.trigger();

      // side-effect close
      handleModalClose();
    };

    const handleModalClose = () => {
      // reset form
      formInstance.reset();

      // close schedule options modal
      setOpenSchedule(false);

      // trigger upstream onClose
      props.onClose?.();
    };

    // outer parent form values for validations
    const [startAtDateValue, startAtTimeValue]: [
      startAtDateValue: string | undefined,
      startAtTimeValue: string | undefined,
    ] = props.parentFormInstance.getValues([startAtDateId, startAtTimeId]);

    useEffect(() => {
      // set default values based on incoming `activity-type-schedule` value
      if (props.value) {
        let values: ActivityTypeSchedule = JSON.parse(String(props.value));

        // default display periodicity form
        setDisplayRepetition(values.frequency || values.frequencyValue ? true : false);

        let defaultValues: PeriodicityFormType = {
          ...values,
          // @ts-ignore
          endDate: values.endDate,
          // @ts-ignore
          endHour: values.endHour,
          // @ts-ignore
          startDate: values.startDate || moment(),
          // @ts-ignore
          frequency: values?.frequency
            ? {
                key: values.frequency,
                label: t(translations.periodicity_form.fields.frequency[values.frequency]),
              }
            : undefined,
          frequencyValue: values.frequencyValue ? Number(values.frequencyValue) : 1,
        };

        // set defaults
        formInstance.reset(defaultValues);

        // trigger validations
        formInstance.trigger();
      }
    }, [formInstance, props.value, t]);

    const startAtDateWatch = props.parentFormInstance.watch(startAtDateId);

    // re-validate on `startDate` change against `endDate` on repetition
    useEffect(() => {
      if (isMoment(startAtDateWatch)) {
        const scheduleValue = props.value;

        try {
          const parsedValue = JSON.parse(String(scheduleValue));
          // if repetition has `endDate` defined
          if (parsedValue.endDate) {
            // run against
            if (startAtDateWatch.isAfter(moment(parsedValue.endDate))) {
              props.parentFormInstance.setError(startAtDateId, {
                message: t(
                  translations.periodicity_form.fields.start_at_date.validations
                    .superior_to_end_date
                ),
              });
            }
          }
        } catch (err) {
          // ignore if non-parseable
        }
      }
    }, [props.parentFormInstance, startAtDateId, props.value, startAtDateWatch, t]);

    // re-trigger validations on `displayRepetition` and on `openSchedule`
    useEffect(() => {
      if (displayRepetition || openSchedule) formInstance.trigger();
    }, [formInstance, displayRepetition, openSchedule]);

    // on opening field modal, evaluate repetition value
    useEffect(() => {
      if (openSchedule) {
        const scheduleValue = props.value;

        try {
          const parsedValue = JSON.parse(String(scheduleValue));

          // if value has key `frequency`, set the display toggle
          if (parsedValue.frequency) {
            setDisplayRepetition(true);
          } else {
            setDisplayRepetition(false);
          }
        } catch (err) {
          // ignore if non-parseable
        }
      }
    }, [props.value, openSchedule]);

    return (
      <Box>
        <ScheduleField {...restProps} openSchedule={() => setOpenSchedule(true)} ref={ref} />
        <Modal
          title={t(translations.schedule_modal.task.title)}
          actions={[
            {
              label: t(translations.modal.cancel),
              type: 'destructive',
              onClick: handleModalClose,
            },
            {
              label: t(translations.modal.save),
              type: 'neutral',
              disabled: displayRepetition ? !formInstance.formState.isValid : false,
              onClick: onSubmit,
            },
          ]}
          open={openSchedule}
          PaperProps={{
            sx: {
              width: '484px',
            },
          }}
        >
          <Stack sx={{width: '100%', gap: '10px', display: 'flex'}}>
            <Switch
              label={t(translations.schedule_modal.trigger_button_label)}
              value={displayRepetition}
              onChange={handleDisplayRepetition}
            />
            <PeriodicityForm
              type={restProps.type}
              displayForm={displayRepetition}
              formInstance={formInstance}
              startAtDateValue={startAtDateValue}
              startAtTimeValue={startAtTimeValue}
            />
          </Stack>
        </Modal>
      </Box>
    );
  }
);
